import { Chip, List, ListItemButton, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

import { Box, SearchField } from '@/components/ui'
import type { FeatureFlag } from '@/definitions/featureFlags'
import { spacing } from '@/themes/tokens'

const ScrollingList = styled(List)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
})

const FlagButton = styled(ListItemButton)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(spacing.xs),
  borderRadius: theme.shape.borderRadius,
}))

interface FeatureFlagListProps {
  flags: FeatureFlag[]
  selectedName: string | null
  search: string
  onSearchChange: (search: string) => void
  onSelect: (name: string) => void
}

/**
 * Every flag in the database, filtered by name.
 *
 * The chips are how far along a flag's rollout is, without opening it. At most
 * one of them is a rollout state: `All orgs` covers new practices too, so a flag
 * on for everyone does not also advertise the narrower step it has passed. Both
 * are held back for a flag that is not live, which reaches nobody it has not
 * already been granted to individually — that gets its own chip instead of a
 * claim it is not honouring.
 */
export const FeatureFlagList = ({
  flags,
  selectedName,
  search,
  onSearchChange,
  onSelect,
}: FeatureFlagListProps) => {
  const term = search.trim().toLowerCase()
  const matching = term
    ? flags.filter((flag) => flag.name.toLowerCase().includes(term))
    : flags

  return (
    <Box variant='flexColumn' gap='md' height='100%'>
      <SearchField
        placeholder='Search flags'
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
      />

      {matching.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No flag matches that name.
        </Typography>
      ) : (
        <ScrollingList disablePadding>
          {matching.map((flag) => (
            <FlagButton
              key={flag.name}
              selected={flag.name === selectedName}
              onClick={() => onSelect(flag.name)}
            >
              <Typography variant='subtitle2'>{flag.name}</Typography>
              <Box variant='flex' gap='xs' fullWidth={false}>
                {flag.enable_all_orgs && flag.enabled && (
                  <Chip size='small' color='success' label='All orgs' />
                )}
                {flag.enable_new_orgs && !flag.enable_all_orgs && flag.enabled && (
                  <Chip size='small' color='info' label='New orgs' />
                )}
                {!flag.enabled && <Chip size='small' label='Not live' />}
              </Box>
            </FlagButton>
          ))}
        </ScrollingList>
      )}
    </Box>
  )
}
